import { useCallback, useState } from 'react';
import { useSubscription } from './useSubscription';
import { useClerkAuth } from './useClerkAuth';
import type { PendingReplayContext } from '../services/replayAuthService';
import { replayAuthService } from '../services/replayAuthService';

export interface ReplaySubscriptionGateReturn {
  // Auth state
  isAuthLoaded: boolean;
  isSignedIn: boolean;
  
  // Subscription state
  plan: string;
  canAccessReplay: boolean;
  canAccessTickReplay: boolean;
  isSubscriptionLoading: boolean;
  
  // Gate function - returns true if user can proceed
  checkReplayAccess: (replayType: 'candle' | 'tick', context?: PendingReplayContext) => boolean;
  
  // Modal controls
  showUpgradePrompt: boolean;
  upgradeFeature: 'replay' | 'tickReplay';
  closeUpgradePrompt: () => void;
  openFullUpgradeModal: () => void;
  
  // Full upgrade modal
  isUpgradeModalOpen: boolean;
  closeUpgradeModal: () => void;
}

export function useReplaySubscriptionGate(): ReplaySubscriptionGateReturn {
  const { isAuthLoaded, isSignedIn, openSignIn } = useClerkAuth();
  const { 
    plan, 
    isLoading: isSubscriptionLoading, 
    checkFeatureAccess,
    openUpgradeModal,
    closeUpgradeModal,
    isUpgradeModalOpen,
  } = useSubscription();

  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<'replay' | 'tickReplay'>('replay');

  const canAccessReplay = checkFeatureAccess('replay');
  const canAccessTickReplay = checkFeatureAccess('tickReplay');

  const checkReplayAccess = useCallback((
    replayType: 'candle' | 'tick',
    context?: PendingReplayContext
  ): boolean => {
    console.log('[useReplaySubscriptionGate] checkReplayAccess called', {
      replayType,
      plan,
      canAccessReplay,
      canAccessTickReplay,
      isAuthLoaded,
      isSignedIn,
      isSubscriptionLoading
    });

    // First check authentication
    if (!isAuthLoaded) {
      console.warn('[useReplaySubscriptionGate] Auth not loaded yet');
      return false;
    }

    // Build return URL with replay context
    const returnUrl = (() => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('replay', '1');
        if (context?.ticker) url.searchParams.set('ticker', context.ticker);
        if (context?.timeframe) url.searchParams.set('tf', context.timeframe);
        return url.toString();
      } catch {
        return undefined;
      }
    })();

    // If not signed in, prompt sign in
    if (!isSignedIn) {
      replayAuthService.setPendingReplayEnter(context);
      openSignIn({ returnUrl });
      return false;
    }

    // Check subscription based on replay type
    if (replayType === 'tick') {
      if (!canAccessTickReplay) {
        setUpgradeFeature('tickReplay');
        setShowUpgradePrompt(true);
        return false;
      }
    } else {
      if (!canAccessReplay) {
        setUpgradeFeature('replay');
        setShowUpgradePrompt(true);
        return false;
      }
    }

    // All checks passed
    return true;
  }, [isAuthLoaded, isSignedIn, openSignIn, canAccessReplay, canAccessTickReplay]);

  const closeUpgradePrompt = useCallback(() => {
    setShowUpgradePrompt(false);
  }, []);

  const openFullUpgradeModal = useCallback(() => {
    setShowUpgradePrompt(false);
    openUpgradeModal();
  }, [openUpgradeModal]);

  return {
    isAuthLoaded,
    isSignedIn,
    plan,
    canAccessReplay,
    canAccessTickReplay,
    isSubscriptionLoading,
    checkReplayAccess,
    showUpgradePrompt,
    upgradeFeature,
    closeUpgradePrompt,
    openFullUpgradeModal,
    isUpgradeModalOpen,
    closeUpgradeModal,
  };
}
